import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import './SearchBar.css';

function SearchBar({ onSelectPlayer, onSelectTeam }) {
  const [query, setQuery] = useState('');
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) {
      setPlayers([]);
      setTeams([]);
      setShowResults(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.search(query, 10);
        if (cancelled) return;
        setPlayers(data.players || []);
        setTeams(data.teams || []);
        setShowResults(true);
      } catch (e) {
        if (!cancelled) { setPlayers([]); setTeams([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pickPlayer = (player) => {
    onSelectPlayer(player);
    setQuery('');
    setShowResults(false);
  };

  const pickTeam = (team) => {
    onSelectTeam(team);
    setQuery('');
    setShowResults(false);
  };

  const hasResults = players.length > 0 || teams.length > 0;

  return (
    <div className="search-bar" ref={boxRef}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { if (hasResults) setShowResults(true); }}
        placeholder="Search players or teams..."
        className="search-input"
      />
      {loading && <div className="search-loading">Searching...</div>}
      {showResults && hasResults && (
        <div className="search-results">
          {players.length > 0 && (
            <>
              <div className="search-section-header">Players</div>
              {players.map((player) => (
                <div key={`p-${player.id}`} className="search-result-item" onClick={() => pickPlayer(player)}>
                  <img
                    src={player.image_url || '/person-placeholder.svg'}
                    alt=""
                    className="person-avatar"
                    style={{ objectPosition: player.image_url ? (player.face_focus || 'center 20%') : 'center' }}
                    onError={(e) => { e.target.src = '/person-placeholder.svg'; }}
                  />
                  <div className="person-details">
                    <div className="person-name">{player.name}</div>
                    <div className="person-info">
                      {[player.nationality, player.position].filter(Boolean).join(' · ') || 'Player'}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {teams.length > 0 && (
            <>
              <div className="search-section-header">Teams</div>
              {teams.map((team) => (
                <div key={`t-${team.id}`} className="search-result-item" onClick={() => pickTeam(team)}>
                  <img
                    src={team.logo_url || '/team-placeholder.svg'}
                    alt=""
                    className="film-poster-thumb"
                    onError={(e) => { e.target.src = '/team-placeholder.svg'; }}
                  />
                  <div className="person-details">
                    <div className="person-name">{team.name}</div>
                    <div className="person-info">
                      {team.team_type === 'national' ? 'National team' : 'Club'}
                      {team.player_count ? ` · ${team.player_count} players` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      {showResults && !hasResults && !loading && (
        <div className="search-results">
          <div className="no-results">No results found</div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
